import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { ProductV2FaqItem } from "@/shared/data/product.v2.data";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useState } from "react";
import Text from "./shared/Text";

/**
 * FaqV2 — centered "Frequently Asked Questions" + CTA row + list of
 * accordion rows. Rows are collapsed by default; clicking the title
 * expands it. Uniform slate-light card background on each row matches the
 * product-page reference.
 */

export type FaqV2Props = {
  title: string;
  ctas?: CtaType[];
  items: ProductV2FaqItem[];
};

const FaqRow = ({
  item,
  isOpen,
  onToggle,
}: {
  item: ProductV2FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <div className="overflow-hidden rounded-2xl bg-slateLight">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left md:px-6 md:py-6"
    >
      <Text
        as="h3"
        variant="title-md"
        className="font-inter !text-[16px] !font-medium text-neutralBlack"
      >
        {item.question}
      </Text>
      <FontAwesomeIcon
        icon={faChevronDown}
        className={classNames(
          "h-4 w-4 shrink-0 text-neutralBlack/60 transition-transform duration-200",
          isOpen ? "rotate-180" : "rotate-0"
        )}
        aria-hidden="true"
      />
    </button>
    {isOpen ? (
      <div className="px-5 pb-6 md:px-6">
        <Text
          as="p"
          variant="body-md"
          className="text-[14px] leading-relaxed text-darkSlate04 md:text-[15px]"
        >
          {item.answer}
        </Text>
      </div>
    ) : null}
  </div>
);

const FaqV2 = ({ title, ctas, items }: FaqV2Props) => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <section className="w-full bg-white">
      <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <div className="mx-auto max-w-6xl">
        <Text
          as="h2"
          variant="display-lg"
          className="text-center text-[32px] text-neutralBlack md:text-[40px] lg:text-[48px]"
        >
          {title}
        </Text>
        {ctas && ctas.length ? (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:mt-10">
            <CtaButtonGroup
              ctas={ctas}
              gap="flex gap-3"
              fullWidthOnMobile={false}
            />
          </div>
        ) : null}
        <div className="mx-auto mt-10 flex max-w-4xl flex-col gap-3 md:mt-12 md:gap-4">
          {items.map((item, idx) => (
            <FaqRow
              key={item.question}
              item={item}
              isOpen={openIdx === idx}
              onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
            />
          ))}
        </div>
      </div>
      </div>
    </section>
  );
};

export default FaqV2;
